<script setup lang="ts">
/**
 * Composant LoadingSpinner - Spinner de chargement animé
 */

import { computed } from 'vue'

import type { LoadingSpinnerSize } from './types'

defineOptions({
  name: 'LoadingSpinner'
})
/**
 * Props du composant LoadingSpinner
 */
interface Props {
  /** Taille du spinner */
  size?: LoadingSpinnerSize
  /** Couleur personnalisée du spinner (optionnel) */
  color?: string
  /** Message à afficher sous le spinner (optionnel) */
  message?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  color: '#00368f', // Couleur principale du projet
  message: undefined
})

/**
 * Classe CSS basée sur la taille
 */
const sizeClass = computed(() => {
  const classes = {
    small: 'loading-spinner--small',
    medium: 'loading-spinner--medium',
    large: 'loading-spinner--large'
  }
  return classes[props.size]
})

/**
 * Style inline pour la couleur personnalisée
 */
const spinnerStyle = computed(() => ({
  borderTopColor: props.color
}))
</script>

<template>
  <div class="loading-spinner" :class="sizeClass">
    <div class="spinner" :style="spinnerStyle"></div>
    <p v-if="message || $slots.default" class="message">
      <slot>{{ message }}</slot>
    </p>
  </div>
</template>

<style scoped>
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.loading-spinner--small {
  --spinner-size: 1rem;
  --spinner-thickness: 2px;
}

.loading-spinner--medium {
  --spinner-size: 2rem;
  --spinner-thickness: 3px;
}

.loading-spinner--large {
  --spinner-size: 3rem;
  --spinner-thickness: 4px;
}

.spinner {
  width: var(--spinner-size);
  height: var(--spinner-size);
  border: var(--spinner-thickness) solid #f0f0f0; /* Couleur de fond du projet */
  border-top: var(--spinner-thickness) solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.message {
  margin: 0;
  font-size: 0.875rem;
  color: #333333; /* Couleur de texte du projet */
  text-align: center;
  font-family: Arial, Helvetica, sans-serif;
}
</style>